/**
 * Creator import — client-side step machine for the three-step
 * upload -> mapping -> review -> importing -> done flow (redesign spec §5).
 * One flow for both import targets: the pipeline sheet and outreach's
 * "Add creators". The target comes from the api client; an outreach upload
 * whose handle column is recognised skips the mapping screen, and an
 * outreach commit is followed on the outreach batch it hands its rows to.
 * Deliberately plain useState/useCallback, not React Query — each step is a
 * one-shot imperative server round-trip that replaces the in-memory row
 * snapshot, not a cacheable/re-fetchable resource.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError, useSSE } from '@discovr/common';
import type {
  ExpectedField,
  ImportBatch,
  ImportBatchRow,
  ImportPreviewResult,
  ImportResult,
  ImportTarget,
  OutreachImportBatch,
  RowChangeSet,
  SheetColumnPreview,
} from '@discovr/types';
import type { ImportBatchApi } from '../api/importBatchMutations';
import { fetchOutreachImport, retryOutreachImport } from '../api/outreachApi';
import { campaignCopy, importErrorCopy, importUploadErrorCopy } from '../../copy';
import { useCampaignCapabilitiesStore } from '../state/useCampaignCapabilitiesStore';
import { buildUndoChangeSet, isEmptyChangeSet, rowsHaveSavedChanges } from './importRowChanges';
import { fetchLastImportResult } from './useLastImportResult';

export type ImportWizardStep = 'upload' | 'mapping' | 'review' | 'importing' | 'done';

/** Status-check fallback while importing (spec §4) — covers a missed SSE. */
export const IMPORT_STATUS_POLL_MS = 5000;
/** How long the "… · Undo" toast stays up after a save (spec §4). */
export const UNDO_TOAST_MS = 4000;
/** A single failed status check is usually a blip; this many in a row is
 *  worth telling the admin about. */
export const IMPORT_STATUS_POLL_MAX_FAILURES = 3;
/** How long the status check keeps going before the admin is told the import
 *  is taking unusually long. The import itself is never stopped. */
export const IMPORT_STATUS_POLL_MAX_MS = 10 * 60 * 1000;
/** A finished import whose result still can't be read after this many checks
 *  is shown as done without its summary, rather than as still running. */
export const IMPORT_RESULT_MISSING_MAX_CHECKS = 3;
/** An outreach import's fallback poll for a dropped SSE stream. */
export const OUTREACH_IMPORT_POLL_MS = 3000;

const CONFLICT_CODE = 'CONFLICT';
const HTTP_CONFLICT = 409;
const LOG_PREFIX = '[ImportWizard]';
const COMMITTED_STATUS: ImportBatch['status'] = 'committed';
/** `details.reason` on a review-grid 409 (backend ImportConflictReason). */
const ALREADY_RUNNING_REASON = 'already_running';
const CHANGED_REASON = 'changed';
export const NO_DEFLECTED_ROWS: number[] = [];
const NO_ACTIVE_IMPORT_DETAIL: ActiveImportDetail = { resumableBatch: null, blockedByTarget: null, runningBatch: null };
const NETWORK_ERROR_CODE = 'NETWORK_ERROR';
const COMMITTING_STATUS: ImportBatch['status'] = 'committing';
const OUTREACH_FINISHED_STATUSES: ReadonlySet<OutreachImportBatch['status']> = new Set(['done', 'failed']);
const OUTREACH_FAILED_STATUS: OutreachImportBatch['status'] = 'failed';
export const OUTREACH_IMPORT_FAILED_MESSAGE =
  "This import stopped before it finished. The creators below were added; the rest weren't. Check the outreach list, then import the missing handles again.";

export const OUTREACH_IMPORT_STALLED_MESSAGE =
  "This import is taking longer than expected. You can close this window — it keeps running, and the creators appear in the outreach list as they're added.";

const COMMIT_ALREADY_RUNNING_MESSAGE = 'This import is already running.';
/** Shown after a review-grid write lost a race and the grid was reloaded. */
export const IMPORT_CHANGED_MESSAGE =
  'This import changed while you were reviewing it. The rows below are its latest state — check them and try again.';
/** Shown after a write found the batch back at column matching. */
export const IMPORT_MOVED_MESSAGE =
  'This import was sent back to column matching, in another tab or by another admin. Match the columns again to continue.';
export const IMPORT_STALLED_MESSAGE =
  'This import is taking longer than expected. You can close this window — it keeps running, and its result will appear under View last import.';
export const STATUS_CHECK_FAILED_MESSAGE =
  "Couldn't load this campaign's import details, so the template isn't available yet. You can still upload a sheet, or close and reopen this window to try again.";
const UPLOAD_FAILED_MESSAGE = "Couldn't upload this sheet. Check the file against Sheet rules and try again.";
const OUTREACH_UPLOAD_FAILED_MESSAGE = "Couldn't read these handles. Check them and try again.";
const UPLOAD_FAILED_MESSAGE_BY_TARGET: Record<ImportTarget, string> = {
  pipeline: UPLOAD_FAILED_MESSAGE,
  outreach: OUTREACH_UPLOAD_FAILED_MESSAGE,
};
const OUTREACH_TARGET: ImportTarget = 'outreach';
const DISCARD_CONFLICT_FAILED_MESSAGE = 'Could not discard the in-progress import — try again';
const MAPPING_FAILED_MESSAGE = 'Could not save column mapping';
const SAVE_ROWS_FAILED_MESSAGE = 'Could not save your changes';
const UNDO_FAILED_MESSAGE = 'Could not undo that change';
const BACK_FAILED_MESSAGE = 'Could not go back';
const COMMIT_FAILED_MESSAGE = 'Could not start the import';
const DISCARD_FAILED_MESSAGE = 'Could not discard this import';
/** Shown in the grid when the wizard reopened onto a commit already running. */
export const IMPORT_STILL_RUNNING_MESSAGE =
  'This import is still running. Its progress is below; you can close this window and it keeps going.';
const MAX_REJECTED_ROWS_SHOWN = 10;
const REJECTED_ROWS_HEADING = "The import couldn't start because these rows were rejected:";
const POLL_FAILED_MESSAGE = "Couldn't check on this import";
const CHANGE_FILE_DISCARD_FAILED_MESSAGE =
  "Couldn't discard the previous file — it's still active. Resume it below, or try Change file again.";

function formatCommitDriftMessage(count: number): string {
  return `The campaign changed while you were reviewing. ${count} creators need a fresh decision.`;
}

function formatReloadFailure(message: string): string {
  return `${message} We couldn't load the latest rows. Close and resume this import to see them.`;
}

/** Which client step a batch's server-side status resumes into. `null` for a
 *  status this flow can't drop back into (a commit already running or
 *  finished) — those fall back to the plain "already in progress, discard?"
 *  card instead of an offered resume. The legacy statuses belong to drafts
 *  created before the redesign; the backend upgrades them to `review` on
 *  load, so they resume into the same grid. */
function stepForBatchStatus(status: ImportBatch['status']): ImportWizardStep | null {
  switch (status) {
    case 'mapping':
      return 'mapping';
    case 'review':
    case 'sheet_review':
    case 'roster_review':
      return 'review';
    case 'committing':
    case 'committed':
      return null;
    default:
      return unknownBatchStatus(status);
  }
}

// A status added to the type without a case above fails to compile here; at
// runtime a status from a newer server is treated like any other non-resumable one.
function unknownBatchStatus(status: never): null {
  console.warn(`${LOG_PREFIX} unknown import status`, status);
  return null;
}

export interface LastRowChange {
  message: string;
  undo: RowChangeSet;
  at: number;
}

export interface ImportWizardState {
  step: ImportWizardStep;
  importId: string | null;
  fileName: string | null;
  sheetColumns: SheetColumnPreview[];
  /** Notes from the last preview response about the sheet (a renamed repeated
   *  column header, say). Empty for a resumed batch. */
  sheetWarnings: string[];
  expectedFields: ExpectedField[];
  /** Data-row count from the last preview response, or from the resumed
   *  batch. Null while unknown (before any upload, or a resumed batch whose
   *  server doesn't say), so the UI never prints a made-up "0". */
  rowCount: number | null;
  /** Sheet header -> field key as last submitted (or as saved on a resumed
   *  batch). The review grid picks its columns from it, and the mapping step
   *  reopens with it after Back. */
  mapping: Record<string, string>;
  /** True while checkStatus's "is an import already active" check is in flight. */
  checkingActive: boolean;
  rows: ImportBatchRow[];
  /** The server read the engagement column as fractions (0.0794) and turned
   *  them into percentages (7.94%); the review says so. */
  engagementReadAsFraction: boolean;
  busy: boolean;
  error: string | null;
  /** The batch blocking a fresh upload (another tab, or an orphan from a
   *  closed session), so the UI can offer discarding or resuming it. */
  activeConflictImportId: string | null;
  /** The batch behind `activeConflictImportId`, non-null only when its status
   *  maps to a resumable step (see `stepForBatchStatus`). */
  resumableBatch: ImportBatch | null;
  /** True when the wizard reopened onto a commit that was already running and
   *  followed it, so the grid says so instead of looking like a fresh import. */
  followingRunningImport: boolean;
  /** Set when the blocking batch belongs to the other import target: the
   *  pipeline and outreach tabs share one active-import slot per campaign, and
   *  a batch can only be resumed or discarded from the tab that started it. */
  blockedByTarget: ImportTarget | null;
  /** Any saved edit, delete or decision since the grid opened — Back to
   *  mapping discards them, so the UI confirms first. */
  hasSavedChanges: boolean;
  lastChange: LastRowChange | null;
  /** Rows a failed commit's roster-drift check sent back to undecided,
   *  re-matched against the campaign creator as it is now. */
  driftRowNumbers: number[];
  importTotal: number;
  /** The result that ended the import, held here so the done screen stays
   *  filled even if the shared store's result is cleared afterwards. */
  doneResult: ImportResult | null;
  /** Rows the finished commit kept at their current values because the
   *  campaign changed mid-review (a partial drift) — read from the committed
   *  batch once the import is done. */
  deflectedRows: number[];
  /** The batch couldn't be read after the import, so which creators kept their
   *  current values is unknown rather than none. */
  deflectedRowsUnconfirmed: boolean;
  /** Outreach target only: the outreach batch the commit handed its rows to.
   *  Null before commit. */
  outreachImportId: number | null;
  /** Outreach target only: rows the commit left out as already on the campaign,
   *  as the server counted them; null when it didn't say. */
  outreachSkippedExisting: number | null;
  /** Outreach target only: that batch's live counts, kept fresh by the hook's
   *  own poll + SSE. A pipeline import tracks progress through the shared
   *  capabilities store instead. */
  outreachBatch: OutreachImportBatch | null;
}

type StateUpdate = (s: ImportWizardState) => ImportWizardState;

/** The polls that can each report "couldn't check on this import". */
type PollId = 'status' | 'outreach' | 'awaiting';

/** What the batch blocking a fresh upload turned out to be. */
interface ActiveImportDetail {
  resumableBatch: ImportBatch | null;
  blockedByTarget: ImportTarget | null;
  /** This tab's own batch with a commit still running: followed, not offered. */
  runningBatch: ImportBatch | null;
}

const initialState: ImportWizardState = {
  step: 'upload',
  importId: null,
  fileName: null,
  sheetColumns: [],
  sheetWarnings: [],
  expectedFields: [],
  rowCount: null,
  mapping: {},
  checkingActive: false,
  rows: [],
  engagementReadAsFraction: false,
  busy: false,
  error: null,
  activeConflictImportId: null,
  resumableBatch: null,
  blockedByTarget: null,
  followingRunningImport: false,
  hasSavedChanges: false,
  lastChange: null,
  driftRowNumbers: [],
  importTotal: 0,
  doneResult: null,
  deflectedRows: NO_DEFLECTED_ROWS,
  deflectedRowsUnconfirmed: false,
  outreachImportId: null,
  outreachSkippedExisting: null,
  outreachBatch: null,
};

/** Every failure is SHOWN in the wizard's own generic wording for the action
 *  that failed. What the server or the network actually said is logged here
 *  for diagnosis and never put in front of the admin. */
function logFailure(shownMessage: string, err: unknown): void {
  console.warn(`${LOG_PREFIX} ${shownMessage}`, err);
}

// An active-import 409 is shown by the conflict card itself, so it leaves no
// error text behind — the card's own failures (discard, change file) need it.
// A request that never got a response (other than a timeout) is usually the
// WAF blocking the file, which the generic wording would misdescribe.
function uploadErrorMessage(err: unknown, conflictId: string | null, target: ImportTarget): string | null {
  if (conflictId !== null) return null;
  const copy = importUploadErrorCopy(err, target);
  if (copy !== undefined) return copy;
  if (err instanceof ApiError && err.code === NETWORK_ERROR_CODE) return campaignCopy.creator.importReview.preview.blocked;
  return UPLOAD_FAILED_MESSAGE_BY_TARGET[target];
}

/** The server's suggested header -> field mapping from a preview. */
function suggestedMapping(columns: SheetColumnPreview[]): Record<string, string> {
  const mapping: Record<string, string> = {};
  for (const column of columns) {
    if (column.suggestedField !== '') mapping[column.header] = column.suggestedField;
  }
  return mapping;
}

function coversRequiredFields(mapping: Record<string, string>, fields: ExpectedField[]): boolean {
  const mapped = new Set(Object.values(mapping));
  return fields.every((field) => !field.required || mapped.has(field.key));
}

/** An outreach sheet maps only a handle and an email; once the server has
 *  recognised the handle column there is nothing left to ask. */
function autoMappingFor(target: ImportTarget, preview: ImportPreviewResult): Record<string, string> | null {
  if (target !== OUTREACH_TARGET) return null;
  const mapping = suggestedMapping(preview.sheetColumns);
  return coversRequiredFields(mapping, preview.expectedFields) ? mapping : null;
}

/** An undo with nothing it can reverse is not offered at all. */
function lastChangeFor(message: string, undo: RowChangeSet): LastRowChange | null {
  if (isEmptyChangeSet(undo)) return null;
  return { message, undo, at: Date.now() };
}

function isOutreachFinished(batch: OutreachImportBatch | null): boolean {
  return batch !== null && OUTREACH_FINISHED_STATUSES.has(batch.status);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isConflict(err: unknown): err is ApiError {
  if (!(err instanceof ApiError)) return false;
  return err.code === CONFLICT_CODE || err.httpStatus === HTTP_CONFLICT;
}

/** P1-33: a 409 means someone already retried it, so track the current run. */
async function retryOrTrackOutreachImport(campaignId: string, outreachImportId: number): Promise<OutreachImportBatch> {
  try {
    return await retryOutreachImport(campaignId, outreachImportId);
  } catch (err) {
    if (!isConflict(err)) throw err;
    return fetchOutreachImport(campaignId, outreachImportId);
  }
}

// Structured `details` the backend attaches to a 409 (ActiveImportConflictDetails /
// RosterDriftConflictDetails) — read field by field, never trusted blindly.
function conflictDetail(err: unknown, key: string): unknown {
  if (!isConflict(err) || !isRecord(err.details)) return undefined;
  return err.details[key];
}

function activeImportIdFrom(err: unknown): string | null {
  const id = conflictDetail(err, 'active_import_id');
  return typeof id === 'string' && id !== '' ? id : null;
}

function isNumberList(value: unknown): value is number[] {
  return Array.isArray(value) && value.every((n) => typeof n === 'number');
}

function driftRowsFrom(err: unknown): number[] | null {
  const rows = conflictDetail(err, 'deflected_rows');
  return isNumberList(rows) ? rows : null;
}

function conflictReasonFrom(err: unknown): string | null {
  const reason = conflictDetail(err, 'reason');
  return typeof reason === 'string' ? reason : null;
}

// The reason decides; the message is only a fallback for a server that
// predates `details.reason` — never matched when a reason is present.
function isAlreadyRunningConflict(err: unknown): boolean {
  if (!isConflict(err)) return false;
  const reason = conflictReasonFrom(err);
  if (reason !== null) return reason === ALREADY_RUNNING_REASON;
  return err.message === COMMIT_ALREADY_RUNNING_MESSAGE;
}

function isChangedConflict(err: unknown): boolean {
  return conflictReasonFrom(err) === CHANGED_REASON;
}

/** The import is running (or already finished) somewhere else: show its
 *  progress instead of a step whose every action would now be refused. An
 *  outreach import is followed on its outreach batch, so its id comes along;
 *  null keeps whatever id the wizard already holds. */
function alreadyRunningUpdate(outreachImportId: number | null = null): StateUpdate {
  return (s) => ({
    ...s,
    busy: false,
    step: 'importing',
    lastChange: null,
    error: COMMIT_ALREADY_RUNNING_MESSAGE,
    outreachImportId: outreachImportId ?? s.outreachImportId,
  });
}

/** The running import's own batch says which outreach batch it handed its
 *  rows to. A failed read still shows the import as running. */
async function runningBatchUpdate(reload: () => Promise<ImportBatch>): Promise<StateUpdate> {
  try {
    const batch = await reload();
    return alreadyRunningUpdate(batch.outreachImportId);
  } catch (reloadErr) {
    console.warn(`${LOG_PREFIX} could not read the running import`, reloadErr);
    return alreadyRunningUpdate();
  }
}

const MESSAGE_FOR_RELOADED_STEP: Partial<Record<ImportWizardStep, string>> = {
  review: IMPORT_CHANGED_MESSAGE,
  mapping: IMPORT_MOVED_MESSAGE,
};

/** What a batch contributes to the review: its rows, and whether the server
 *  read the engagement column as fractions. */
function batchRows(batch: ImportBatch) {
  return { rows: batch.rows, engagementReadAsFraction: batch.engagementReadAsFraction };
}

/** Puts the wizard on the step the reloaded batch is actually at, showing
 *  what the server has saved. */
function reloadedBatchUpdate(batch: ImportBatch): StateUpdate {
  const step = stepForBatchStatus(batch.status);
  if (step === null) return alreadyRunningUpdate(batch.outreachImportId);
  return (s) => ({
    ...s,
    busy: false,
    lastChange: null,
    step,
    importId: batch.id,
    ...batchRows(batch),
    mapping: batch.mapping,
    hasSavedChanges: rowsHaveSavedChanges(batch.rows),
    error: MESSAGE_FOR_RELOADED_STEP[step] ?? IMPORT_CHANGED_MESSAGE,
  });
}

/** After a write lost a race, the batch is reloaded from the server so the
 *  wizard shows what is actually saved, on the step the batch is really at.
 *  The undo is dropped: it was built against rows that are no longer current.
 *  If the reload itself fails the wizard stays where it is and says so. */
async function reloadBatchUpdate(reload: () => Promise<ImportBatch>): Promise<StateUpdate> {
  try {
    return reloadedBatchUpdate(await reload());
  } catch (reloadErr) {
    console.warn(`${LOG_PREFIX} reload after a conflict failed`, reloadErr);
    return (s) => ({ ...s, busy: false, lastChange: null, error: formatReloadFailure(IMPORT_CHANGED_MESSAGE) });
  }
}

/** What a write that lost a race should turn the wizard into: the import's
 *  progress when it is already running, the batch's real step when it changed
 *  underneath this tab. Null for any other failure. */
async function conflictRecoveryUpdate(err: unknown, reload: () => Promise<ImportBatch>): Promise<StateUpdate | null> {
  if (isAlreadyRunningConflict(err)) return runningBatchUpdate(reload);
  if (isChangedConflict(err)) return reloadBatchUpdate(reload);
  return null;
}

/** Best-effort: the done screen already has the import result; the batch
 *  only adds which creators a partial drift kept at their current values.
 *  A failed read resolves to null, so the done screen says it couldn't confirm
 *  rather than holding up a finished import or silently claiming "none". */
async function fetchDeflectedRows(api: ImportBatchApi, campaignId: string, importId: string): Promise<number[] | null> {
  try {
    const batch = await api.getImportBatch(campaignId, importId);
    return batch.deflectedRows;
  } catch (err) {
    console.warn(`${LOG_PREFIX} could not read which creators a drift kept as they were`, err);
    return null;
  }
}

/** The server's own count when it sends one; otherwise the rows it sent, or
 *  null: a mapping-stage batch comes with no rows, which is not "0 rows" (a
 *  server that says 0 with no rows is a legacy batch it can't count either). */
function batchRowCount(batch: ImportBatch): number | null {
  const count = batch.rowCount ?? batch.rows.length;
  return count > 0 ? count : null;
}

/** Follows a commit that was already running when the wizard opened: the
 *  importing step, with the progress the status poll and SSE keep filling in. */
function followRunningUpdate(batch: ImportBatch): StateUpdate {
  return (s) => ({
    ...s,
    step: 'importing',
    importId: batch.id,
    fileName: batch.fileName,
    ...batchRows(batch),
    expectedFields: batch.expectedFields,
    sheetColumns: sheetColumnsFromBatch(batch),
    sheetWarnings: [],
    rowCount: batchRowCount(batch),
    mapping: batch.mapping,
    outreachImportId: batch.outreachImportId,
    activeConflictImportId: null,
    resumableBatch: null,
    blockedByTarget: null,
    followingRunningImport: true,
    busy: false,
    error: null,
    lastChange: null,
    driftRowNumbers: [],
  });
}

function sheetColumnsFromBatch(batch: ImportBatch): SheetColumnPreview[] {
  return batch.headers.map((header) => ({
    header,
    sampleValue: '',
    suggestedField: batch.mapping[header] ?? '',
  }));
}

async function driftRecoveryUpdate(driftRows: number[], reload: () => Promise<ImportBatch>): Promise<StateUpdate> {
  const message = formatCommitDriftMessage(driftRows.length);
  const reopenReview = { busy: false, step: 'review' as const, driftRowNumbers: driftRows, lastChange: null };
  try {
    const batch = await reload();
    return (s) => ({ ...s, ...reopenReview, ...batchRows(batch), error: message });
  } catch (reloadErr) {
    // Still reopen the grid: staying on "importing" or retrying commit would
    // hit the same 409 forever, so explain both failures instead.
    console.warn(`${LOG_PREFIX} reload after a roster drift failed`, reloadErr);
    return (s) => ({ ...s, ...reopenReview, error: formatReloadFailure(message) });
  }
}

interface RejectedRow {
  row: number;
  username: string;
  reason: string;
}

function rejectedRowFrom(value: unknown): RejectedRow | null {
  if (!isRecord(value) || typeof value.row !== 'number') return null;
  return {
    row: value.row,
    username: typeof value.username === 'string' ? value.username : '',
    reason: typeof value.reject_reason === 'string' ? value.reject_reason : '',
  };
}

/** The per-row reasons a refused commit carries (`details.rejected`), read
 *  field by field; empty when the server sent none. */
function rejectedRowsFrom(err: unknown): RejectedRow[] {
  if (!(err instanceof ApiError) || !isRecord(err.details)) return [];
  const rejected = err.details.rejected;
  if (!Array.isArray(rejected)) return [];
  return rejected.map(rejectedRowFrom).filter((r): r is RejectedRow => r !== null);
}

function rejectedRowLine({ row, username, reason }: RejectedRow): string {
  const who = username === '' ? `Row ${row}` : `Row ${row} · @${username}`;
  return reason === '' ? who : `${who}: ${reason}`;
}

/** Which rows the server refused and why, capped so a sheet of thousands
 *  doesn't fill the screen. */
export function formatRejectedRows(rows: RejectedRow[]): string {
  const shown = rows.slice(0, MAX_REJECTED_ROWS_SHOWN).map(rejectedRowLine);
  const more = rows.length - shown.length;
  const lines = more > 0 ? [...shown, `and ${more} more`] : shown;
  return [REJECTED_ROWS_HEADING, ...lines].join('\n');
}

async function commitFailureUpdate(err: unknown, target: ImportTarget, reload: () => Promise<ImportBatch>): Promise<StateUpdate> {
  const driftRows = driftRowsFrom(err);
  if (driftRows) return driftRecoveryUpdate(driftRows, reload);
  // The guarded review -> committing flip lost to another commit (double
  // click, second tab), or the batch changed underneath this review.
  const recovery = await conflictRecoveryUpdate(err, reload);
  if (recovery) return recovery;
  logFailure(COMMIT_FAILED_MESSAGE, err);
  const rejected = rejectedRowsFrom(err);
  const message = rejected.length > 0 ? formatRejectedRows(rejected) : (importErrorCopy(err, target) ?? COMMIT_FAILED_MESSAGE);
  return (s) => ({ ...s, busy: false, error: message });
}

/** Where the commit is: still running, or finished — with its result, or
 *  with a result that can't be read (not stored, or not visible to this role). */
type CommitProgress = { committed: false } | { committed: true; result: ImportResult | null };

async function fetchCommitProgress(api: ImportBatchApi, campaignId: string, importId: string): Promise<CommitProgress> {
  const batch = await api.getImportBatch(campaignId, importId);
  if (batch.status !== COMMITTED_STATUS) return { committed: false };
  return { committed: true, result: await fetchLastImportResult(campaignId) };
}

/** The import finished but its summary can't be read: end on the done screen
 *  without one rather than showing a finished import as still running. */
function doneWithoutResult(s: ImportWizardState): ImportWizardState {
  if (s.step !== 'importing') return s;
  return { ...s, step: 'done', error: null, doneResult: null };
}

interface ImportStatusPoll {
  fetchProgress: () => Promise<CommitProgress>;
  onResult: (result: ImportResult) => void;
  /** The import finished, but its result can't be read. */
  onResultUnavailable: () => void;
  /** The import still hasn't finished after IMPORT_STATUS_POLL_MAX_MS. */
  onStalled: () => void;
  onRepeatedFailure: (err: unknown) => void;
  /** A check succeeded after onRepeatedFailure had been reported. */
  onRecovered: () => void;
}

const IMPORT_STATUS_POLL_MAX_CHECKS = Math.ceil(IMPORT_STATUS_POLL_MAX_MS / IMPORT_STATUS_POLL_MS);

/** Polls until the commit's outcome is known; returns the stop function.
 *  It always ends: with the result, with a finished import whose result can't
 *  be read, or by giving up after IMPORT_STATUS_POLL_MAX_MS. Overlapping
 *  checks are skipped so a slow response never stacks requests. */
function startImportStatusPoll(poll: ImportStatusPoll): () => void {
  let stopped = false;
  let inFlight = false;
  let checks = 0;
  let consecutiveFailures = 0;
  let failureReported = false;
  let missingResultChecks = 0;

  function stop() {
    stopped = true;
    clearInterval(timer);
  }

  // Reported once per streak of failures, not again on every later tick.
  function recordFailure(err: unknown) {
    consecutiveFailures += 1;
    if (stopped || consecutiveFailures !== IMPORT_STATUS_POLL_MAX_FAILURES) return;
    failureReported = true;
    poll.onRepeatedFailure(err);
  }

  function recordSuccess() {
    consecutiveFailures = 0;
    if (!failureReported) return;
    failureReported = false;
    if (!stopped) poll.onRecovered();
  }

  function settle(progress: CommitProgress) {
    if (!progress.committed) return;
    if (progress.result) {
      stop();
      poll.onResult(progress.result);
      return;
    }
    missingResultChecks += 1;
    if (missingResultChecks < IMPORT_RESULT_MISSING_MAX_CHECKS) return;
    stop();
    poll.onResultUnavailable();
  }

  function giveUpIfOverdue() {
    if (stopped || checks < IMPORT_STATUS_POLL_MAX_CHECKS) return;
    stop();
    poll.onStalled();
  }

  async function checkOnce() {
    if (stopped || inFlight) return;
    inFlight = true;
    checks += 1;
    try {
      const progress = await poll.fetchProgress();
      recordSuccess();
      if (!stopped) settle(progress);
    } catch (err) {
      recordFailure(err);
    } finally {
      inFlight = false;
      giveUpIfOverdue();
    }
  }

  const timer = setInterval(() => {
    void checkOnce();
  }, IMPORT_STATUS_POLL_MS);
  return stop;
}

export interface BoundedPoll {
  intervalMs: number;
  /** Resolves true once there is nothing left to wait for. */
  check: () => Promise<boolean>;
  onStalled: () => void;
  onRepeatedFailure: (err: unknown) => void;
  /** A check succeeded after onRepeatedFailure had been reported. */
  onRecovered: () => void;
}

/** Checks now and then every interval; returns the stop function. Ends when
 *  `check` resolves true or after IMPORT_STATUS_POLL_MAX_MS. A failed check is
 *  retried, and reported once per streak of IMPORT_STATUS_POLL_MAX_FAILURES. */
export function startBoundedPoll(poll: BoundedPoll): () => void {
  const maxChecks = Math.ceil(IMPORT_STATUS_POLL_MAX_MS / poll.intervalMs);
  let stopped = false;
  let inFlight = false;
  let checks = 0;
  let consecutiveFailures = 0;
  let failureReported = false;

  function stop() {
    stopped = true;
    clearInterval(timer);
  }

  async function checkOnce() {
    if (stopped || inFlight) return;
    inFlight = true;
    checks += 1;
    try {
      const finished = await poll.check();
      consecutiveFailures = 0;
      if (failureReported && !stopped) poll.onRecovered();
      failureReported = false;
      if (finished && !stopped) stop();
    } catch (err) {
      consecutiveFailures += 1;
      if (!stopped && consecutiveFailures === IMPORT_STATUS_POLL_MAX_FAILURES) {
        failureReported = true;
        poll.onRepeatedFailure(err);
      }
    } finally {
      inFlight = false;
      if (!stopped && checks >= maxChecks) {
        stop();
        poll.onStalled();
      }
    }
  }

  const timer = setInterval(() => {
    void checkOnce();
  }, poll.intervalMs);
  void checkOnce();
  return stop;
}

export interface ImportWizardOptions {
  /** The roster changed under the page without an `import.complete` event
   *  reaching it (the status check found the finished import instead). */
  onRosterStale?: () => void;
}

export function useImportWizard(campaignId: string, api: ImportBatchApi, options: ImportWizardOptions = {}) {
  const { target } = api;
  const onRosterStaleRef = useRef(options.onRosterStale);
  onRosterStaleRef.current = options.onRosterStale;
  const isOutreach = target === OUTREACH_TARGET;
  const [state, setState] = useState<ImportWizardState>(initialState);
  const setImportResult = useCampaignCapabilitiesStore((s) => s.setImportResult);
  const setImportProgress = useCampaignCapabilitiesStore((s) => s.setImportProgress);
  const clearImportSignals = useCallback(() => {
    setImportResult(null);
    setImportProgress(null);
  }, [setImportResult, setImportProgress]);

  // Bumped whenever the wizard is closed or reset, so a request that was in
  // flight at that point can't write its late result into the fresh wizard.
  const generationRef = useRef(0);

  // One busy/error envelope for every plain server round-trip: the request's
  // result is applied only on success, and a failure is shown AND rethrown.
  const runServerStep = useCallback(
    async <T>(request: () => Promise<T>, apply: (result: T) => Partial<ImportWizardState>, failureMessage: string) => {
      const generation = generationRef.current;
      setState((s) => ({ ...s, busy: true, error: null }));
      try {
        const result = await request();
        if (generation === generationRef.current) setState((s) => ({ ...s, ...apply(result), busy: false }));
      } catch (err) {
        logFailure(failureMessage, err);
        if (generation === generationRef.current) {
          setState((s) => ({ ...s, busy: false, error: importErrorCopy(err, target) ?? failureMessage }));
        }
        throw err;
      }
    },
    [target],
  );

  // runServerStep for writes to a batch another tab or admin may also be
  // working on. A conflict means the batch moved on underneath this request:
  // the wizard then follows it (its real step, or the running import) instead
  // of being left on a screen whose every action is refused.
  const runBatchStep = useCallback(
    async <T>(
      importId: string,
      request: () => Promise<T>,
      apply: (result: T) => Partial<ImportWizardState>,
      failureMessage: string,
    ) => {
      const generation = generationRef.current;
      try {
        await runServerStep(request, apply, failureMessage);
      } catch (err) {
        // Landing on `importing` here, not through commit(), so a result left
        // in the store by an earlier import must not end this one.
        if (isAlreadyRunningConflict(err)) clearImportSignals();
        const reload = async () => {
          const batch = await api.getImportBatch(campaignId, importId);
          if (stepForBatchStatus(batch.status) === null) clearImportSignals();
          return batch;
        };
        const recovery = await conflictRecoveryUpdate(err, reload);
        if (recovery && generation === generationRef.current) setState(recovery);
        throw err;
      }
    },
    [campaignId, api, runServerStep, clearImportSignals],
  );

  // Best-effort: fetch the batch behind an active-import id. It is offered for
  // resuming only if its status is one this client can resume into and it was
  // started from this tab's target; a batch from the other tab is reported
  // instead, and one of this tab's whose commit is running is followed (it
  // can't be discarded). A failure just means no Resume button — Discard is
  // still offered for the same id.
  const describeActiveImport = useCallback(
    async (importId: string): Promise<ActiveImportDetail> => {
      try {
        const batch = await api.getImportBatch(campaignId, importId);
        if (batch.target !== target) return { ...NO_ACTIVE_IMPORT_DETAIL, blockedByTarget: batch.target };
        if (batch.status === COMMITTING_STATUS) return { ...NO_ACTIVE_IMPORT_DETAIL, runningBatch: batch };
        return { ...NO_ACTIVE_IMPORT_DETAIL, resumableBatch: stepForBatchStatus(batch.status) ? batch : null };
      } catch (err) {
        console.warn(`${LOG_PREFIX} could not load the active import to offer resuming it`, err);
        return NO_ACTIVE_IMPORT_DETAIL;
      }
    },
    [campaignId, api, target],
  );

  // A running commit is followed straight away; anything else is held as the
  // conflict card's batch. A result left in the store by an earlier import
  // must not end the followed one at once.
  const applyActiveDetail = useCallback(
    (activeImportId: string | null, detail: ActiveImportDetail): StateUpdate => {
      if (detail.runningBatch) {
        setImportResult(null);
        return followRunningUpdate(detail.runningBatch);
      }
      const { resumableBatch, blockedByTarget } = detail;
      return (s) => ({ ...s, activeConflictImportId: activeImportId, resumableBatch, blockedByTarget });
    },
    [setImportResult],
  );

  // Fires when the wizard opens: surfaces an already-active batch before any
  // file is picked and loads the expected columns. Never rejects — the real
  // active-import guard still applies server-side on the upload itself — but
  // a failure is shown: without the columns the template can't be built.
  const checkStatus = useCallback(async () => {
    const generation = generationRef.current;
    setState((s) => ({ ...s, checkingActive: true, error: null }));
    try {
      const status = await api.getImportStatus(campaignId);
      const detail = status.activeImportId ? await describeActiveImport(status.activeImportId) : NO_ACTIVE_IMPORT_DETAIL;
      if (generation !== generationRef.current) return;
      const applyDetail = applyActiveDetail(status.activeImportId, detail);
      setState((s) => ({ ...applyDetail(s), checkingActive: false, expectedFields: status.expectedFields }));
    } catch (err) {
      logFailure(STATUS_CHECK_FAILED_MESSAGE, err);
      if (generation === generationRef.current) setState((s) => ({ ...s, checkingActive: false, error: STATUS_CHECK_FAILED_MESSAGE }));
    }
  }, [campaignId, api, describeActiveImport, applyActiveDetail]);

  // An upload conflict normally names the import that blocks it. When the
  // server couldn't, the status endpoint is asked instead, so the admin still
  // gets the Resume / Discard card rather than a conflict they can't clear.
  const findActiveImportId = useCallback(
    async (uploadErr: unknown): Promise<string | null> => {
      if (!isConflict(uploadErr)) return null;
      try {
        const status = await api.getImportStatus(campaignId);
        return status.activeImportId;
      } catch (statusErr) {
        console.warn(`${LOG_PREFIX} could not find the import blocking this upload`, statusErr);
        return null;
      }
    },
    [campaignId, api],
  );

  // A recognised outreach sheet goes straight on to review. A failed
  // auto-mapping leaves the admin on the mapping screen to pick the columns.
  const applyAutoMapping = useCallback(
    async (importId: string, mapping: Record<string, string>, generation: number) => {
      try {
        const batch = await api.submitMapping(campaignId, importId, mapping);
        if (generation !== generationRef.current) return;
        setState((s) => ({ ...s, busy: false, step: 'review', ...batchRows(batch), mapping, hasSavedChanges: false }));
      } catch (err) {
        logFailure(MAPPING_FAILED_MESSAGE, err);
        if (generation !== generationRef.current) return;
        setState((s) => ({ ...s, busy: false, step: 'mapping', error: importErrorCopy(err, target) ?? MAPPING_FAILED_MESSAGE }));
      }
    },
    [campaignId, api, target],
  );

  const uploadFile = useCallback(
    async (file: File) => {
      const generation = generationRef.current;
      setState((s) => ({ ...s, busy: true, error: null, activeConflictImportId: null }));
      let result: ImportPreviewResult;
      try {
        result = await api.previewImport(campaignId, file);
      } catch (err) {
        logFailure(UPLOAD_FAILED_MESSAGE, err);
        const conflictId = activeImportIdFrom(err) ?? (await findActiveImportId(err));
        const detail = conflictId ? await describeActiveImport(conflictId) : NO_ACTIVE_IMPORT_DETAIL;
        if (generation !== generationRef.current) throw err;
        const applyDetail = applyActiveDetail(conflictId, detail);
        setState((s) => ({ ...applyDetail(s), busy: false, error: detail.runningBatch ? null : uploadErrorMessage(err, conflictId, target) }));
        throw err;
      }
      // The wizard was closed or moved to another campaign meanwhile: this
      // sheet's batch belongs to nobody now.
      if (generation !== generationRef.current) return;
      const autoMapping = autoMappingFor(target, result);
      // An auto-mapped upload stays on the upload step, busy, until the
      // mapping lands, so the mapping screen never flashes past.
      const autoMapped = autoMapping !== null;
      setState((s) => ({
        ...s,
        busy: autoMapped,
        step: autoMapped ? s.step : 'mapping',
        importId: result.importId,
        fileName: file.name,
        sheetColumns: result.sheetColumns,
        sheetWarnings: result.warnings ?? [],
        expectedFields: result.expectedFields,
        rowCount: result.rowCount,
      }));
      if (autoMapping) await applyAutoMapping(result.importId, autoMapping, generation);
    },
    [campaignId, api, target, describeActiveImport, findActiveImportId, applyAutoMapping, applyActiveDetail],
  );

  // Explicit "Discard and try again": a failed DELETE must be reported and
  // leave the conflict in place, never look like success.
  const discardActiveConflict = useCallback(async () => {
    const conflictId = state.activeConflictImportId;
    if (!conflictId) return;
    const generation = generationRef.current;
    setState((s) => ({ ...s, busy: true, error: null }));
    try {
      await api.discardImport(campaignId, conflictId);
    } catch (err) {
      // A commit that started since the card was drawn can't be discarded;
      // follow it instead of reporting a failure the admin can't act on.
      const detail = isConflict(err) ? await describeActiveImport(conflictId) : NO_ACTIVE_IMPORT_DETAIL;
      if (generation !== generationRef.current) throw err;
      if (detail.runningBatch) {
        setState(applyActiveDetail(conflictId, detail));
        return;
      }
      logFailure(DISCARD_CONFLICT_FAILED_MESSAGE, err);
      setState((s) => ({ ...s, busy: false, error: importErrorCopy(err, target) ?? DISCARD_CONFLICT_FAILED_MESSAGE }));
      throw err;
    }
    if (generation !== generationRef.current) return;
    setState((s) => ({ ...s, busy: false, error: null, activeConflictImportId: null, resumableBatch: null, blockedByTarget: null }));
  }, [campaignId, api, target, state.activeConflictImportId, describeActiveImport, applyActiveDetail]);

  // Rehydrates from the already-fetched `resumableBatch` — synchronous. A
  // `mapping` resume has no sample values (only the original /preview had
  // them); the admin can still map columns manually.
  const resumeActiveImport = useCallback(() => {
    const batch = state.resumableBatch;
    if (!batch || batch.target !== target) return;
    const step = stepForBatchStatus(batch.status);
    if (!step) return;
    setState((s) => ({
      ...s,
      step,
      importId: batch.id,
      fileName: batch.fileName,
      ...batchRows(batch),
      expectedFields: batch.expectedFields,
      sheetColumns: sheetColumnsFromBatch(batch),
      sheetWarnings: [],
      rowCount: batchRowCount(batch),
      mapping: batch.mapping,
      activeConflictImportId: null,
      resumableBatch: null,
      blockedByTarget: null,
      error: null,
      hasSavedChanges: rowsHaveSavedChanges(batch.rows),
      lastChange: null,
      driftRowNumbers: [],
    }));
  }, [state.resumableBatch, target]);

  const submitMapping = useCallback(
    async (mapping: Record<string, string>) => {
      const importId = state.importId;
      if (!importId) return;
      await runBatchStep(
        importId,
        () => api.submitMapping(campaignId, importId, mapping),
        (batch) => ({ step: 'review', ...batchRows(batch), mapping, hasSavedChanges: false, lastChange: null, driftRowNumbers: [] }),
        MAPPING_FAILED_MESSAGE,
      );
    },
    [campaignId, api, state.importId, runBatchStep],
  );

  // The undo is computed from the rows BEFORE the save; on failure the
  // server saved nothing, so the rows are left exactly as they were.
  const saveRows = useCallback(
    async (changes: RowChangeSet, message: string) => {
      const importId = state.importId;
      if (!importId) return;
      const undo = buildUndoChangeSet(state.rows, changes);
      await runBatchStep(
        importId,
        () => api.saveRows(campaignId, importId, changes),
        (batch) => ({ ...batchRows(batch), hasSavedChanges: true, lastChange: lastChangeFor(message, undo) }),
        SAVE_ROWS_FAILED_MESSAGE,
      );
    },
    [campaignId, api, state.importId, state.rows, runBatchStep],
  );

  const undoLastChange = useCallback(async () => {
    const importId = state.importId;
    const lastChange = state.lastChange;
    if (!importId || !lastChange) return;
    await runBatchStep(
      importId,
      () => api.saveRows(campaignId, importId, lastChange.undo),
      (batch) => ({ ...batchRows(batch), lastChange: null }),
      UNDO_FAILED_MESSAGE,
    );
  }, [campaignId, api, state.importId, state.lastChange, runBatchStep]);

  const dismissLastChange = useCallback(() => {
    setState((s) => ({ ...s, lastChange: null }));
  }, []);

  // Upload and Mapping both sit at the batch's `mapping` status server-side,
  // so moving between them needs no server call.
  const continueToMapping = useCallback(() => {
    setState((s) => (s.step === 'upload' && s.importId ? { ...s, step: 'mapping' } : s));
  }, []);
  const backToUpload = useCallback(() => {
    setState((s) => (s.step === 'mapping' ? { ...s, step: 'upload' } : s));
  }, []);

  // review -> mapping server-side, or Mapping's own Continue would 400
  // against the stale review status. Saved row changes are discarded there.
  const backToMapping = useCallback(async () => {
    const importId = state.importId;
    if (!importId) return;
    await runBatchStep(
      importId,
      () => api.stepBackImport(campaignId, importId),
      (batch) => ({ step: 'mapping', ...batchRows(batch), hasSavedChanges: false, lastChange: null, driftRowNumbers: [] }),
      BACK_FAILED_MESSAGE,
    );
  }, [campaignId, api, state.importId, runBatchStep]);

  // Guards a double click before the busy state has rendered.
  const committingRef = useRef(false);
  const commit = useCallback(
    async ({ skipBlocking }: { skipBlocking: boolean }) => {
      const importId = state.importId;
      if (!importId || committingRef.current) return;
      committingRef.current = true;
      const generation = generationRef.current;
      setState((s) => ({ ...s, busy: true, error: null }));
      // A result/progress still in the store from a PREVIOUS import would
      // make the 'importing' step's markDone effect jump straight to 'done'.
      clearImportSignals();
      try {
        const started = await api.commitImport(campaignId, importId, { skipBlocking });
        if (generation !== generationRef.current) return;
        setState((s) => ({
          ...s,
          busy: false,
          step: 'importing',
          outreachImportId: started.outreachImportId ?? null,
          outreachSkippedExisting: started.skippedExisting ?? null,
          importTotal: started.totalRows,
          lastChange: null,
          driftRowNumbers: [],
          deflectedRows: NO_DEFLECTED_ROWS,
          deflectedRowsUnconfirmed: false,
        }));
      } catch (err) {
        const update = await commitFailureUpdate(err, target, () => api.getImportBatch(campaignId, importId));
        if (generation === generationRef.current) setState(update);
        throw err;
      } finally {
        committingRef.current = false;
      }
    },
    [campaignId, api, target, state.importId, clearImportSignals],
  );

  // Each poll reports and clears its own failure: the banner stays while any
  // poll is still failing, and a stall or other message set meanwhile is a
  // different problem that recovery never clears.
  const failingPollsRef = useRef(new Set<PollId>());
  const pollFailed = useCallback((id: PollId, err: unknown) => {
    failingPollsRef.current.add(id);
    logFailure(POLL_FAILED_MESSAGE, err);
    setState((s) => ({ ...s, error: POLL_FAILED_MESSAGE }));
  }, []);
  const pollRecovered = useCallback((id: PollId) => {
    failingPollsRef.current.delete(id);
    if (failingPollsRef.current.size > 0) return;
    setState((s) => (s.error === POLL_FAILED_MESSAGE ? { ...s, error: null } : s));
  }, []);
  const scopedPoll = useCallback((id: PollId, stop: () => void) => () => {
    stop();
    failingPollsRef.current.delete(id);
  }, []);

  // Status-check fallback: the SSE result event normally ends 'importing';
  // if it's missed, this finds the committed batch and stores the result,
  // and the wizard's existing importResult effect then calls markDone.
  const { step, importId } = state;
  useEffect(() => {
    if (isOutreach || step !== 'importing' || !importId) return undefined;
    return scopedPoll('status', startImportStatusPoll({
      fetchProgress: () => fetchCommitProgress(api, campaignId, importId),
      onResult: (result) => {
        setImportResult(result);
        onRosterStaleRef.current?.();
      },
      onResultUnavailable: () => setState(doneWithoutResult),
      onStalled: () => setState((s) => ({ ...s, error: IMPORT_STALLED_MESSAGE })),
      onRepeatedFailure: (err) => pollFailed('status', err),
      onRecovered: () => pollRecovered('status'),
    }));
  }, [isOutreach, step, importId, campaignId, api, setImportResult, pollFailed, pollRecovered, scopedPoll]);

  const markDone = useCallback((result: ImportResult) => {
    setState((s) => (s.step === 'importing' ? { ...s, step: 'done', error: null, doneResult: result } : s));
  }, []);

  // Runs as markDone lands on `done` (an effect keeps markDone synchronous
  // and stable for the wizard's result effect). A partial roster drift turns
  // the drifted rows into keep_existing server-side, and only the committed
  // batch says which — the done screen needs them to count those creators
  // as kept, not updated.
  useEffect(() => {
    if (isOutreach || step !== 'done' || !importId) return undefined;
    let cancelled = false;
    void fetchDeflectedRows(api, campaignId, importId).then((deflectedRows) => {
      if (cancelled) return;
      setState((s) => ({ ...s, deflectedRows: deflectedRows ?? NO_DEFLECTED_ROWS, deflectedRowsUnconfirmed: deflectedRows === null }));
    });
    return () => {
      cancelled = true;
    };
  }, [isOutreach, step, importId, campaignId, api]);

  // An outreach import is followed on its outreach batch: there is no central
  // store switch for its events. SSE first, with a poll as the fallback for a
  // dropped stream.
  const outreachBatchId = step === 'importing' ? state.outreachImportId : null;

  const refreshOutreachBatch = useCallback(
    async (batchId: number): Promise<OutreachImportBatch> => {
      const batch = await fetchOutreachImport(campaignId, batchId);
      setState((s) => (s.step === 'importing' ? { ...s, outreachBatch: batch } : s));
      return batch;
    },
    [campaignId],
  );

  const onOutreachPollStalled = useCallback(() => {
    setState((s) => ({ ...s, error: OUTREACH_IMPORT_STALLED_MESSAGE }));
  }, []);

  useEffect(() => {
    if (outreachBatchId === null) return undefined;
    return scopedPoll('outreach', startBoundedPoll({
      intervalMs: OUTREACH_IMPORT_POLL_MS,
      check: async () => isOutreachFinished(await refreshOutreachBatch(outreachBatchId)),
      onStalled: onOutreachPollStalled,
      onRepeatedFailure: (err) => pollFailed('outreach', err),
      onRecovered: () => pollRecovered('outreach'),
    }));
  }, [outreachBatchId, refreshOutreachBatch, onOutreachPollStalled, pollFailed, pollRecovered, scopedPoll]);

  // The hand-off to the outreach batch can land after this tab started
  // following (another tab's commit, or a reload that raced it), so until the
  // import batch names it the batch itself is what gets watched.
  const awaitingOutreachId = isOutreach && step === 'importing' && importId !== null && state.outreachImportId === null;
  useEffect(() => {
    if (!awaitingOutreachId || !importId) return undefined;
    let committedWithoutId = 0;
    return scopedPoll('awaiting', startBoundedPoll({
      intervalMs: OUTREACH_IMPORT_POLL_MS,
      check: async () => {
        const batch = await api.getImportBatch(campaignId, importId);
        if (batch.outreachImportId !== null) {
          const followId = batch.outreachImportId;
          setState((s) => (s.step === 'importing' ? { ...s, outreachImportId: followId } : s));
          return true;
        }
        if (batch.status !== COMMITTED_STATUS) return false;
        committedWithoutId += 1;
        if (committedWithoutId < IMPORT_RESULT_MISSING_MAX_CHECKS) return false;
        setState(doneWithoutResult);
        return true;
      },
      onStalled: onOutreachPollStalled,
      onRepeatedFailure: (err) => pollFailed('awaiting', err),
      onRecovered: () => pollRecovered('awaiting'),
    }));
  }, [awaitingOutreachId, importId, campaignId, api, onOutreachPollStalled, pollFailed, pollRecovered, scopedPoll]);

  // useSSE needs a stable handler, so the current batch id is read via a ref.
  // Re-fetching (not building from the payload) keeps the item ledger that
  // "Send first touch to all" reads; the payload only carries counts.
  const outreachBatchIdRef = useRef(outreachBatchId);
  outreachBatchIdRef.current = outreachBatchId;
  const onOutreachImportEvent = useCallback(
    (payload: { import_id?: number }) => {
      const batchId = outreachBatchIdRef.current;
      if (batchId === null || payload.import_id !== batchId) return;
      refreshOutreachBatch(batchId).catch((err: unknown) => {
        console.warn(`${LOG_PREFIX} could not refresh the outreach import`, err);
      });
    },
    [refreshOutreachBatch],
  );
  useSSE('outreach_import_progress', outreachBatchId !== null, onOutreachImportEvent);
  useSSE('outreach_import_done', outreachBatchId !== null, onOutreachImportEvent);

  const outreachFinished = isOutreachFinished(state.outreachBatch);
  const outreachFailed = state.outreachBatch?.status === OUTREACH_FAILED_STATUS;
  useEffect(() => {
    if (!outreachFinished) return;
    const error = outreachFailed ? OUTREACH_IMPORT_FAILED_MESSAGE : null;
    setState((s) => (s.step === 'importing' ? { ...s, step: 'done', error } : s));
  }, [outreachFinished, outreachFailed]);

  // P1-33: back to 'importing' so the outreach poll/SSE track the re-run. The
  // retry response carries no item ledger, so the created ids are kept until
  // the first refresh; a result landing after a reset is dropped.
  const retryFailedOutreachImport = useCallback(async () => {
    const outreachImportId = state.outreachImportId;
    if (!isOutreach || outreachImportId === null) return;
    setState((s) => ({ ...s, busy: true, error: null }));
    try {
      const batch = await retryOrTrackOutreachImport(campaignId, outreachImportId);
      setState((s) => {
        if (s.outreachImportId !== outreachImportId) return s;
        const createdEntryIds = s.outreachBatch?.createdEntryIds ?? [];
        return { ...s, busy: false, step: 'importing', outreachBatch: { ...batch, createdEntryIds } };
      });
    } catch (err) {
      console.warn(`${LOG_PREFIX} outreach import retry failed`, { campaignId, outreachImportId, err });
      const error = importErrorCopy(err, OUTREACH_TARGET) ?? campaignCopy.creator.importReview.outreachFailed.error;
      setState((s) => (s.outreachImportId === outreachImportId ? { ...s, busy: false, error } : s));
    }
  }, [campaignId, isOutreach, state.outreachImportId]);

  // Also clears the shared import-progress/result store — a lingering result
  // re-triggers CreatorsTab's fallback ImportStatusModal once the wizard closes.
  const reset = useCallback(() => {
    generationRef.current += 1;
    setState(initialState);
    clearImportSignals();
  }, [clearImportSignals]);

  // A wizard left open across a campaign switch would keep the old batch and
  // save it against the new campaign id. A fresh state (and any request still
  // in flight for the old campaign made stale) is the only safe thing to hold.
  const campaignIdRef = useRef(campaignId);
  useEffect(() => {
    if (campaignIdRef.current === campaignId) return;
    campaignIdRef.current = campaignId;
    generationRef.current += 1;
    setState(initialState);
    clearImportSignals();
  }, [campaignId, clearImportSignals]);

  // Closing never throws away review work: a batch that reached the review
  // grid stays on the server, and the next open offers to resume it. Only an
  // upload that hasn't been matched to columns yet has nothing worth keeping,
  // so it is discarded as before.
  const close = useCallback(async () => {
    const { importId: closingId, step: closingStep } = state;
    reset();
    if (!closingId || (closingStep !== 'upload' && closingStep !== 'mapping')) return;
    try {
      await api.discardImport(campaignId, closingId);
    } catch (err) {
      // Best-effort — the admin is leaving and local state is already reset.
      // The next open finds the leftover draft and offers to resume or
      // discard it, and the batch TTL reclaims it otherwise.
      console.warn(`${LOG_PREFIX} could not discard the draft on close`, err);
    }
  }, [state, campaignId, api, reset]);

  // The explicit "Discard import": unlike close, a failed DELETE is reported
  // and the import stays open, never looking like it was thrown away.
  // Resolves true once discarded; false when the commit had already started
  // (in another tab), so the import is followed instead of discarded.
  const discardImport = useCallback(async (): Promise<boolean> => {
    const discardingId = state.importId;
    if (!discardingId) return false;
    const generation = generationRef.current;
    setState((s) => ({ ...s, busy: true, error: null }));
    try {
      await api.discardImport(campaignId, discardingId);
    } catch (err) {
      const detail = isConflict(err) ? await describeActiveImport(discardingId) : NO_ACTIVE_IMPORT_DETAIL;
      if (generation !== generationRef.current) throw err;
      if (detail.runningBatch) {
        setState(applyActiveDetail(discardingId, detail));
        return false;
      }
      logFailure(DISCARD_FAILED_MESSAGE, err);
      setState((s) => ({ ...s, busy: false, error: importErrorCopy(err, target) ?? DISCARD_FAILED_MESSAGE }));
      throw err;
    }
    if (generation === generationRef.current) reset();
    return true;
  }, [state.importId, campaignId, api, target, reset, describeActiveImport, applyActiveDetail]);

  // Picks a different file after Back from mapping. Unlike closeAndDiscard,
  // a failed discard is visible (checkStatus re-finds the old batch), so it
  // is reported rather than left looking like "Change file" did nothing.
  const changeFile = useCallback(async () => {
    const previousId = state.importId;
    const generation = generationRef.current;
    setState((s) => ({ ...s, busy: true, error: null }));
    let discardErr: unknown = null;
    if (previousId) {
      try {
        await api.discardImport(campaignId, previousId);
      } catch (err) {
        discardErr = err;
      }
    }
    if (generation !== generationRef.current) return;
    reset();
    await checkStatus();
    if (!discardErr) return;
    logFailure(CHANGE_FILE_DISCARD_FAILED_MESSAGE, discardErr);
    setState((s) => ({ ...s, error: CHANGE_FILE_DISCARD_FAILED_MESSAGE }));
  }, [campaignId, api, state.importId, reset, checkStatus]);

  return {
    ...state,
    target,
    // Derived, not stored, so it can never drift from resumeActiveImport.
    resumableStep: state.resumableBatch ? stepForBatchStatus(state.resumableBatch.status) : null,
    checkStatus,
    uploadFile,
    discardActiveConflict,
    resumeActiveImport,
    continueToMapping,
    backToUpload,
    changeFile,
    submitMapping,
    saveRows,
    undoLastChange,
    dismissLastChange,
    backToMapping,
    commit,
    markDone,
    retryFailedOutreachImport,
    reset,
    close,
    discardImport,
  };
}
